import React from 'react'
import { useTranslation } from 'react-i18next'
import { getCountriesWithCommonFirst, alpha3ToAlpha2, SupportedLanguage, COMMON_COUNTRY_CODES } from '../../lib/countries'
import { Select } from './select'
import { cn } from '../../lib/utils'

// Import flag components dynamically
import * as Flags from 'country-flag-icons/react/3x2'

interface CountrySelectProps {
  value?: string // ISO 3166-1 alpha-3 code
  onChange: (value: string) => void
  disabled?: boolean
  placeholder?: string
  className?: string
  id?: string
}

// Type for the Flags module
type FlagComponent = React.ComponentType<{ className?: string }>
type FlagsModule = Record<string, FlagComponent>

/**
 * Get flag component by alpha-2 code
 */
function getFlagComponent(alpha3: string): FlagComponent | null {
  if (!alpha3) return null
  const alpha2 = alpha3ToAlpha2(alpha3)
  if (!alpha2) return null
  const flagsModule = Flags as FlagsModule
  return flagsModule[alpha2.toUpperCase()] || null
}

export function CountrySelect({
  value,
  onChange,
  disabled,
  placeholder,
  className,
  id,
}: CountrySelectProps) {
  const { i18n, t } = useTranslation()
  const lang = (i18n.language?.substring(0, 2) || 'en') as SupportedLanguage
  const countries = getCountriesWithCommonFirst(lang)

  const FlagIcon = value ? getFlagComponent(value) : null

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {FlagIcon && (
        <div className="flex-shrink-0 w-6 h-4 rounded overflow-hidden border border-border">
          <FlagIcon className="w-full h-full object-cover" />
        </div>
      )}
      <Select
        id={id}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="flex-1"
      >
        <option value="">{placeholder || t('common.address.selectCountry', 'Select country...')}</option>
        {/* Common countries section */}
        <optgroup label={t('common.address.commonCountries', 'Common')}>
          {countries
            .filter(c => COMMON_COUNTRY_CODES.includes(c.alpha3))
            .map(country => (
              <option key={country.alpha3} value={country.alpha3}>
                {country.name}
              </option>
            ))}
        </optgroup>
        {/* All other countries */}
        <optgroup label={t('common.address.allCountries', 'All Countries')}>
          {countries
            .filter(c => !COMMON_COUNTRY_CODES.includes(c.alpha3))
            .map(country => (
              <option key={country.alpha3} value={country.alpha3}>
                {country.name}
              </option>
            ))}
        </optgroup>
      </Select>
    </div>
  )
}

/**
 * Display component for showing a country with its flag
 */
export function CountryDisplay({
  alpha3,
  showFlag = true,
  className
}: {
  alpha3?: string
  showFlag?: boolean
  className?: string
}) {
  const { i18n } = useTranslation()
  const lang = (i18n.language?.substring(0, 2) || 'en') as SupportedLanguage

  if (!alpha3) return null

  const FlagIcon = showFlag ? getFlagComponent(alpha3) : null
  const countries = getCountriesWithCommonFirst(lang)
  const country = countries.find(c => c.alpha3 === alpha3)
  const countryName = country?.name || alpha3

  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      {FlagIcon && (
        <span className="flex-shrink-0 w-4 h-3 rounded overflow-hidden border border-border">
          <FlagIcon className="w-full h-full object-cover" />
        </span>
      )}
      <span>{countryName}</span>
    </span>
  )
}
